<template>
  <div class="builder-artist-login">
    <v-card>
      <v-card-title> Artist login </v-card-title>
      <v-card-text>
        <div>
          <v-btn color="success" @click="openLoginPopup">
            <v-icon class="mr-4">mdi-spotify</v-icon>
            Login Spotify
          </v-btn>
        </div>

        <!-- Display the login URL with copy-to-clipboard functionality -->
        <div class="mt-6">
          <v-text-field
            v-model="shareableUrl"
            label="External login URL (click to copy)"
            readonly
            append-icon="mdi-content-copy"
            @click="copyShareableUrl"
            class="cursor-pointer"
            helper-text="Share this URL with artists to connect their Spotify account"

          />
          <v-alert type="info" outlined>
            External URL can be shared with artist to connect their Spotify account
            without direct access to their credentials
          </v-alert>
        </div>

        <div class="mt-6">
          <strong>Connected accounts:</strong>
          <div class="mt-2 artists" v-if="campaignConnectedArtists.length">
            <v-row v-for="artist in campaignConnectedArtists" :key="artist.id">
              <v-col>
                {{ artist.display_name }} ({{ artist.streaming_service_id }})
                <v-chip
                  v-if="isPlaylistOwner(artist)"
                  color="success"
                  small
                  class="ml-2"
                >
                  <v-icon left small>mdi-check-circle</v-icon>
                  Playlist owner
                </v-chip>
              </v-col>
              <v-col>
                <v-btn color="error" @click="revokeArtist(artist.id)"
                  >Revoke</v-btn
                >
              </v-col>
            </v-row>
          </div>
          <div class="mt-2" v-else>No connected accounts</div>
          <v-alert
            v-if="playlistOwnerMismatch"
            type="warning"
            outlined
            class="mt-4"
            cy-id="owner-mismatch-warning"
          >
            The selected artist playlist is owned by Spotify account
            <strong>{{ playlistOwnerLabel }}</strong>, which is not
            connected to this campaign. Playlist updates will fail until the
            artist connects with the account that owns the playlist.
          </v-alert>
          <div class="mt-6" v-if="campaignConnectedArtists">
            <v-row>
              <v-col cols="12">
                <strong>Connected Artist playlist:</strong>
              </v-col>
              <v-col cols="12">
                <BuilderPlaylistTile
                  v-if="campaignConnectedArtistPlaylist"
                  v-model="campaignConnectedArtistPlaylist"
                >
                  <v-btn
                    color="warning"
                    dark
                    slot="button"
                    cy-id="playlist-remove"
                    @click="removePlaylist"
                  >
                    Remove
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </BuilderPlaylistTile>
                <BuilderPlaylistSearch v-else @add="addPlaylist" />
              </v-col>
            </v-row>
          </div>
          <div class="mt-6" v-else>No connected playlists</div>
        </div>
      </v-card-text>
    </v-card>
    <v-snackbar v-model="snackbarCopied" color="success" timeout="3000" class="mb-8">
      URL copied to clipboard
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import host from '@/utils/host'
import {
  Component, Vue, Watch,
} from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'
import { ArtistPlaylistOwner, ConnectedArtist } from '../../types'
import BuilderPlaylistSearch from './BuilderPlaylistSearch.vue'
import BuilderPlaylistTile from './BuilderPlaylistTile.vue'
import cms from '@/cms'

@Component({
  components: {
    BuilderPlaylistSearch,
    BuilderPlaylistTile,
  }
})
export default class BuilderArtistLogin extends Vue {
  @Getter campaignId!: number
  @Getter campaignConnectedArtists!: ConnectedArtist[]
  @Getter campaignConnectedArtistPlaylistOwner!: ArtistPlaylistOwner | null

  @Action('CAMPAIGN_ACCOUNT_LOAD') loadArtists!: () => Promise<void>
  @Action('CAMPAIGN_ACCOUNT_REVOKE') revokeArtist!: (id: number) => Promise<void>

  snackbarCopied = false

  get campaignConnectedArtistPlaylist (): string {
    return this.$store.getters.campaignConnectedArtistPlaylist
  }

  set campaignConnectedArtistPlaylist (data: string) {
    this.$store.commit('SET_CAMPAIGN_CONNECTED_ARTIST_PLAYLIST', data)
  }

  // Owner info from the server for the currently saved playlist, or null when
  // the selection was changed locally and the owner isn't known yet.
  get currentPlaylistOwner (): ArtistPlaylistOwner | null {
    const owner = this.campaignConnectedArtistPlaylistOwner
    if (!owner || owner.playlistId !== this.campaignConnectedArtistPlaylist) {
      return null
    }
    return owner
  }

  get playlistOwnerAccountId (): string {
    return this.currentPlaylistOwner?.accountId || ''
  }

  ownerDisplayName = ''

  // The owner's display name isn't stored on the campaign, only the account
  // id, so resolve it from the playlist object via the CMS Spotify proxy.
  @Watch('currentPlaylistOwner', { immediate: true })
  async fetchOwnerDisplayName () {
    const owner = this.currentPlaylistOwner
    this.ownerDisplayName = ''
    if (!owner) {
      return
    }
    try {
      const playlist = await cms.api.spotify.getPlaylist(owner.playlistId)
      if (this.currentPlaylistOwner?.playlistId === owner.playlistId) {
        this.ownerDisplayName = playlist.owner.display_name || ''
      }
    } catch (e) {
      // The name is cosmetic; the warning still shows the account id
      console.error(e)
    }
  }

  get playlistOwnerLabel (): string {
    if (this.ownerDisplayName) {
      return `${this.ownerDisplayName} (${this.playlistOwnerAccountId})`
    }
    return this.playlistOwnerAccountId
  }

  isPlaylistOwner (artist: ConnectedArtist): boolean {
    return !!this.currentPlaylistOwner &&
      artist.streaming_service_id === this.currentPlaylistOwner.accountId
  }

  get playlistOwnerMismatch (): boolean {
    if (!this.currentPlaylistOwner) {
      return false
    }
    return !this.campaignConnectedArtists.some(a => this.isPlaylistOwner(a))
  }

  created () {
    this.loadArtists()
  }

  async copyShareableUrl () {
    await navigator.clipboard.writeText(this.shareableUrl)
    this.snackbarCopied = true
  }

  get shareableUrl () {
    return cms.campaign.url + '/#/connect-account'
  }

  get loginUrl () {
    const redirectUri = encodeURIComponent(`${window.location.origin}/v2/connectcallback.html`)
    return `${host}/api/auth/spotify/connect?redirect_uri=${redirectUri}&campaign_id=${this.campaignId}`
  }

  async openLoginPopup () {
    window.addEventListener('message', this.handleConnectCallback)
    window.open(this.loginUrl, 'Connect account', 'height=640,width=960,toolbar=no,menubar=no,scrollbars=no,location=no,status=no')
  }

  handleConnectCallback (message: any) {
    const { data } = message
    if (data.type !== 'community-playlist-connect') {
      // Check cause CMS sends other messages that we ignore here
      return
    }

    // load artists from server
    this.loadArtists()

    // destroy listener when done
    window.removeEventListener('message', this.handleConnectCallback)
  }

  addPlaylist (id: string) {
    this.campaignConnectedArtistPlaylist = id
  }

  removePlaylist () {
    this.campaignConnectedArtistPlaylist = ''
  }
}
</script>

<style lang="scss" scoped>
</style>
